import { createConsumer, Consumer, Subscription } from '@rails/actioncable';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('WebSocket', LogLevel.WARN);

// Interface for subscription management
export interface StreamSubscription {
  unsubscribe: () => void;
}

// Store subscription parameters for reconnection
interface SubscriptionParams {
  signedStreamName: string;
  documentId: number;
  streamKey: string;
  onUpdate: (data: unknown) => void;
}

// Singleton WebSocket service with reconnection support
class WebSocketService {
  private cable: Consumer | null = null;
  private subscriptions: Map<string, Subscription> = new Map();
  private subscriptionParams: Map<string, SubscriptionParams> = new Map();
  private url: string | null = null;
  private reconnectAttempts: number = 0;
  private reconnectTimer: number | null = null;
  private maxReconnectAttempts: number = 10;
  private baseReconnectDelay: number = 1000; // 1 second

  initialize(config: { url: string }): void {
    // Don't initialize when offline
    if (!navigator.onLine) {
      logger.info('Skipping initialization - offline');
      this.url = config.url; // Store URL for later reconnection
      this.setupNetworkListeners();
      return;
    }

    // Don't re-initialize if already initialized
    if (this.cable) {
      return;
    }

    try {
      this.url = config.url;
      // Convert http(s) to ws(s) and append /cable
      const wsUrl = config.url.replace(/^https?/, (match) => match === 'https' ? 'wss' : 'ws') + '/cable';
      this.cable = createConsumer(wsUrl);
      this.reconnectAttempts = 0; // Reset reconnection counter on successful init
      logger.info('Initialized', { url: wsUrl });
      this.setupNetworkListeners();
    } catch (error) {
      logger.error('Failed to create consumer', error);
      this.cable = null;
      this.scheduleReconnect();
    }
  }

  private setupNetworkListeners(): void {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = (): void => {
    logger.info('Network online, attempting to reconnect');
    this.reconnect();
  };

  private handleOffline = (): void => {
    logger.info('Network offline, disconnecting');
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) {
      return; // Already scheduled
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached', {
        attempts: this.reconnectAttempts
      });
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s...
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );

    logger.info('Scheduling reconnection', {
      attempt: this.reconnectAttempts + 1,
      delayMs: delay
    });

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, delay);
  }

  private reconnect(): void {
    if (!navigator.onLine) {
      logger.info('Skipping reconnect - offline');
      return;
    }

    if (!this.url) {
      logger.warn('Cannot reconnect - no URL configured');
      return;
    }

    this.reconnectAttempts++;
    logger.info('Attempting reconnection', {
      attempt: this.reconnectAttempts
    });

    // Disconnect existing cable if any
    if (this.cable) {
      try {
        this.cable.disconnect();
      } catch (error) {
        logger.error('Error disconnecting old cable', error);
      }
      this.cable = null;
    }

    // Clear existing subscriptions (they'll be recreated)
    this.subscriptions.clear();

    // Re-initialize
    try {
      const wsUrl = this.url.replace(/^https?/, (match) => match === 'https' ? 'wss' : 'ws') + '/cable';
      this.cable = createConsumer(wsUrl);
      this.reconnectAttempts = 0; // Reset on successful connection
      logger.info('Reconnected successfully', { url: wsUrl });

      // Resubscribe to all previous subscriptions
      this.resubscribeAll();
    } catch (error) {
      logger.error('Reconnection failed', error);
      this.cable = null;
      this.scheduleReconnect();
    }
  }

  private resubscribeAll(): void {
    if (!this.cable) {
      return;
    }

    logger.info('Resubscribing to all channels', {
      count: this.subscriptionParams.size
    });

    // Resubscribe using stored parameters
    this.subscriptionParams.forEach((params, key) => {
      try {
        const subscription = this.cable!.subscriptions.create(
          { channel: 'Turbo::StreamsChannel', signed_stream_name: params.signedStreamName },
          {
            connected: () => logger.info('Reconnected to channel', { documentId: params.documentId }),
            disconnected: () => {
              logger.warn('Disconnected from channel', { documentId: params.documentId });
              // Schedule reconnection on disconnect
              if (navigator.onLine) {
                this.scheduleReconnect();
              }
            },
            received: (data: unknown) => {
              params.onUpdate(data);
            },
            rejected: () => {
              logger.error('Subscription rejected after reconnection', {
                documentId: params.documentId
              });
            }
          }
        );

        this.subscriptions.set(key, subscription);
      } catch (error) {
        logger.error('Failed to resubscribe', { key, error });
      }
    });
  }

  subscribeToDocument(
    signedStreamName: string,
    documentId: number,
    onUpdate: (data: unknown) => void,
    streamKey: string = 'main'
  ): StreamSubscription {
    // A document can have several streams (the main offline stream plus one per
    // sensitive-identifier permission), so the key is scoped by streamKey.
    const key = `document-${documentId}-${streamKey}`;

    // Prevent duplicate subscriptions
    if (this.subscriptions.has(key)) {
      logger.warn('Already subscribed', { documentId, streamKey });
      return { unsubscribe: () => this.unsubscribe(key) };
    }

    // Store subscription parameters for reconnection
    this.subscriptionParams.set(key, {
      signedStreamName,
      documentId,
      streamKey,
      onUpdate
    });

    // Can't subscribe without a cable connection
    if (!this.cable) {
      logger.warn('No cable connection available', { documentId, streamKey });
      return { unsubscribe: () => this.unsubscribe(key) };
    }

    const subscription = this.cable.subscriptions.create(
      { channel: 'Turbo::StreamsChannel', signed_stream_name: signedStreamName },
      {
        connected: () => logger.info('Connected to channel', { documentId, streamKey }),
        disconnected: () => {
          logger.warn('Disconnected from channel', { documentId, streamKey });
          // Schedule reconnection on disconnect
          if (navigator.onLine) {
            this.scheduleReconnect();
          }
        },
        received: (data: unknown) => {
          onUpdate(data);
        },
        // A rejected subscription is treated as "no access": we log and simply
        // never receive updates on this stream — the same observable outcome as
        // a user the server never handed this stream name to.
        rejected: () => logger.warn('Subscription rejected (treated as no-access)', { documentId, streamKey })
      }
    );

    this.subscriptions.set(key, subscription);
    return { unsubscribe: () => this.unsubscribe(key) };
  }

  private unsubscribe(key: string): void {
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
      logger.info('Unsubscribed', { key });
    }

    // Remove subscription parameters
    this.subscriptionParams.delete(key);
  }

  disconnect(): void {
    // Clean up reconnection timer
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Clean up network listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    if (this.cable) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
      this.subscriptions.clear();
      this.subscriptionParams.clear();
      this.cable.disconnect();
      this.cable = null;
      this.reconnectAttempts = 0;
      logger.info('Disconnected');
    }
  }
}

export const websocketService = new WebSocketService();
